import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { taskAPI, workspaceAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { TaskDetailFullModal } from '../components/TaskDetailFullModal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { 
    cn, 
    formatDate, 
    priorityConfig, 
    isOverdue,
    getInitials
} from '../lib/utils';
import {
    CheckCircle2,
    Clock,
    Calendar,
    UserCheck,
    Loader2,
    ArrowUpDown,
    User,
    Circle,
    PlayCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
    CONST_WORKFLOW_CONFIG,
} from '../lib/utils';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

// Default statuses for task detail modal
const DEFAULT_STATUSES = [
    { name: 'To Do', color: '#6B7280' },
    { name: 'In Progress', color: '#3B82F6' },
    { name: 'Review', color: '#6366F1' },
    { name: 'Done', color: '#10B981' }
];

export default function AssignByMePage() {
    const containerRef = useRef(null);
    const sentinelRef = useRef(null);
    const { user } = useAuth();
    const { 
        currentWorkspace, 
        fetchWorkspaces, 
        selectWorkspace,
        workspaces,
        members,
        isReadOnly,
        refreshTasks 
    } = useWorkspace();
    
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingTask, setLoadingTask] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [activeTab, setActiveTab] = useState('not_started');
    const [sortBy, setSortBy] = useState('priority');
    const [stats, setStats] = useState({
        total_tasks: 0,
        not_started: 0,
        active: 0,
    });
    
    const [isReset, setIsReset] = useState(true);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(6);

    const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3, none: 4 };

    useEffect(() => {
        initPage();
    }, []);

    useEffect(() => {
        if (!currentWorkspace || !user) return;

        if (isReset) {
            fetchStats();
            fetchTasks(true);
            setIsReset(false);
        } else {
            fetchTasks(false);
        }
    }, [page, currentWorkspace, user, activeTab]);

    useEffect(() => {
        if (currentWorkspace && user) {
            setLoading(true);
            setHasMore(true);
            setIsReset(true);
            setPage(1);
        }
    }, [currentWorkspace, user]);

    const initPage = async () => {
        if (!currentWorkspace) {
            const ws = await fetchWorkspaces();
            if (ws.length > 0) {
                await selectWorkspace(ws[0]);
            }
        }
        setLoading(false);
    };

    const fetchStats = async () => {
        try {
            const response = await workspaceAPI.getAssignedByMeStats(currentWorkspace?.id);
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch assigned-by-me stats:', error);
        }
    };

    const getStatusGroupByActiveTab = () => {
        switch (activeTab) {
            case 'not_started':
                return CONST_WORKFLOW_CONFIG.notstarted.name;
            case 'active':
                return CONST_WORKFLOW_CONFIG.active.name;
            default:
                return null;
        }
    };

    const fetchTasks = async (changedTab = true) => {
        if (loadingTask || !currentWorkspace) return;
        if (changedTab) setLoadingTask(true);
        const status = getStatusGroupByActiveTab();
        try {
            const response = await taskAPI.getAssignedByMe(currentWorkspace?.id, {
                group: status,
                page: page,
                size: pageSize
            });
            
            const newTasks = response.data;
            if (changedTab) {
                setTasks(newTasks);
                setIsReset(false);
            } else {
                setTasks(prev => [...prev, ...newTasks]);
            }

            if (newTasks.length < pageSize) {
                setHasMore(false);
            }
        } catch (error) {
            console.error('Failed to fetch assigned-by-me tasks:', error);
        } finally {
            setLoadingTask(false);
            setLoading(false);
        }
    };

    const loadMore = useCallback(() => {
        setPage(prev => prev + 1);
    }, []);

    useInfiniteScroll(containerRef, sentinelRef, loadMore, hasMore, loadingTask);

    const handleTabChange = (val) => {
        setActiveTab(val);
        setHasMore(true);
        setIsReset(true);
        setPage(1);
    };

    const handleTaskClick = (task) => {
        setSelectedTask(task);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedTask(null);
        fetchTasks();
    };

    const handleQuickStatusChange = async (taskId) => {
        try {
            await taskAPI.quickChangeStatus(taskId);
            fetchTasks();
            toast.success('Status updated');
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    // Sort tasks
    const sortedTasks = [...tasks].sort((a, b) => {
        switch (sortBy) {
            case 'priority':
                const priorityA = priorityOrder[a.priority?.toLowerCase()] ?? 4;
                const priorityB = priorityOrder[b.priority?.toLowerCase()] ?? 4;
                return priorityA - priorityB;
            case 'due_date':
                if (!a.due_date) return 1;
                if (!b.due_date) return -1;
                return new Date(a.due_date) - new Date(b.due_date);
            case 'created_at':
                return new Date(b.created_at) - new Date(a.created_at);
            default:
                const defA = priorityOrder[a.priority?.toLowerCase()] ?? 4;
                const defB = priorityOrder[b.priority?.toLowerCase()] ?? 4;
                return defA - defB;
        }
    });

    if (loading) {
        return (
            <Layout title="Assign By Me">
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                </div>
            </Layout>
        );
    }

    if (!currentWorkspace) {
        return (
            <Layout title="Assign By Me">
                <div className="p-6 text-center text-brand-text-muted">
                    Pilih workspace untuk melihat task yang Anda assign
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Assign By Me" className="!p-0 !overflow-hidden">
            <div className="w-full h-[calc(100vh-80px)] overflow-y-auto" ref={containerRef}>
                <div className="p-6 space-y-6">
                    {/* Header */}
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-brand-text-primary flex items-center gap-2">
                            <UserCheck className="w-6 h-6 text-brand-primary" strokeWidth={1.5} />
                            Assign By Me
                        </h1>
                        <p className="text-brand-text-secondary mt-1">
                            Task yang Anda assign ke tim
                        </p>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <Card className="border-brand-border shadow-card">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-brand-primary/10 rounded-lg">
                                        <UserCheck className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-brand-text-primary">{stats.total_tasks}</p>
                                        <p className="text-sm text-brand-text-muted">Total Task</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="border-brand-border shadow-card">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-gray-100 rounded-lg">
                                        <Circle className="w-5 h-5 text-gray-500" strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-brand-text-primary">{stats.not_started}</p>
                                        <p className="text-sm text-brand-text-muted">Not Started</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="border-brand-border shadow-card">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-100 rounded-lg">
                                        <PlayCircle className="w-5 h-5 text-blue-500" strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-blue-500">{stats.active}</p>
                                        <p className="text-sm text-brand-text-muted">Active</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Tabs & Sort */}
                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full sm:w-auto">
                            <TabsList>
                                <TabsTrigger value="not_started">Not Started ({stats.not_started})</TabsTrigger>
                                <TabsTrigger value="active">Active ({stats.active})</TabsTrigger>
                                <TabsTrigger value="all">All ({stats.total_tasks})</TabsTrigger>
                            </TabsList>
                        </Tabs>

                        <div className="flex items-center gap-2 mb-2">
                            <ArrowUpDown className="w-4 h-4 text-brand-text-muted" />
                            <Select value={sortBy} onValueChange={setSortBy}>
                                <SelectTrigger className="w-40">
                                    <SelectValue placeholder="Urutkan" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="priority">Prioritas</SelectItem>
                                    <SelectItem value="due_date">Due Date</SelectItem>
                                    <SelectItem value="created_at">Terbaru</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Task List */}
                    {sortedTasks.length === 0 ? (
                        <Card className="border-brand-border shadow-card mt-4">
                            <CardContent className="p-12 text-center">
                                <UserCheck className="w-12 h-12 mx-auto text-brand-text-muted mb-4" strokeWidth={1.5} />
                                <p className="text-brand-text-muted">
                                    {activeTab === 'all' 
                                        ? 'Tidak ada task yang Anda assign' 
                                        : `Tidak ada task ${activeTab === 'not_started' ? 'Not Started' : 'Active'}`}
                                </p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {sortedTasks.map((task) => (
                                <Card 
                                    key={task.id}
                                    className={cn(
                                        "border-brand-border shadow-card cursor-pointer hover:shadow-md transition-all",
                                        isOverdue(task.due_date) && task.status_group !== 'Done' && "border-l-4 border-l-red-400"
                                    )}
                                    data-testid={`assign-task-${task.id}`}>

                                    <CardContent className="p-4">
                                        <div className="flex items-center gap-4">
                                            {/* Status Checkbox */}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleQuickStatusChange(task.id);
                                                }}
                                                className={cn(
                                                    'w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors flex-shrink-0',
                                                    task.status_group === 'Done' 
                                                        ? 'bg-brand-success border-brand-success' 
                                                        : 'border-brand-border hover:border-brand-primary'
                                                )}
                                            >
                                                {task.status_group === 'Done' && (
                                                    <CheckCircle2 className="w-3 h-3 text-white" strokeWidth={2} />
                                                )}
                                            </button>

                                            <div className="flex items-center gap-4 w-full" onClick={() => handleTaskClick(task)}>
                                                {/* Task Info */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-xs text-brand-text-muted font-mono">
                                                            {task.task_code || `#${task.task_number}`}
                                                        </span>
                                                        {task.priority !== 'none' && (
                                                            <span className={cn(
                                                                "px-2 py-0.5 rounded text-xs",
                                                                priorityConfig[task.priority]?.bgClass
                                                            )}>
                                                                {priorityConfig[task.priority]?.label}
                                                            </span>
                                                        )}
                                                        {task.category && (
                                                            <span className="px-2 py-0.5 rounded text-xs bg-brand-accent-soft text-brand-primary">
                                                                {task.category}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className={cn(
                                                        "font-medium text-brand-text-primary",
                                                        task.status_group === 'Done' && "line-through text-brand-text-muted"
                                                    )}>
                                                        {task.title}
                                                    </p>
                                                    
                                                    {/* Assignees Info */}
                                                    {task.assignees && task.assignees.length > 0 && (
                                                        <div className="flex items-center gap-2 mt-2">
                                                            <User className="w-3 h-3 text-brand-text-muted" />
                                                            <div className="flex -space-x-2">
                                                                {task.assignees.slice(0, 3).map((assignee, idx) => (
                                                                    <div 
                                                                        key={assignee.id || idx}
                                                                        className="w-6 h-6 rounded-full bg-brand-primary text-white text-xs flex items-center justify-center border-2 border-white"
                                                                        title={assignee.name}
                                                                    >
                                                                        {getInitials(assignee.name)}
                                                                    </div>
                                                                ))}
                                                                {task.assignees.length > 3 && (
                                                                    <div className="w-6 h-6 rounded-full bg-brand-accent-soft text-brand-text-muted text-xs flex items-center justify-center border-2 border-white">
                                                                        +{task.assignees.length - 3}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <span className="text-xs text-brand-text-muted">
                                                                {task.assignees.map(a => a.name).join(', ')}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Due Date */}
                                                {task.due_date && (
                                                    <div className={cn(
                                                        "flex items-center gap-1 text-sm shrink-0",
                                                        isOverdue(task.due_date) && task.status_group !== 'Done' 
                                                            ? "text-red-500" 
                                                            : "text-brand-text-muted"
                                                    )}>
                                                        <Calendar className="w-4 h-4" strokeWidth={1.5} />
                                                        {formatDate(task.due_date)}
                                                    </div>
                                                )}

                                                {/* Status Badge */}
                                                <span className={cn(
                                                    "px-3 py-1 rounded-full text-xs font-medium shrink-0",
                                                    task.status_group === 'Done' && "bg-green-100 text-green-700",
                                                    task.status_group === 'Active' && "bg-blue-100 text-blue-700",
                                                    task.status_group === 'Not Started' && "bg-gray-100 text-gray-700",
                                                    task.status_group === 'Review' && "bg-purple-100 text-purple-700"
                                                )}>
                                                    {task.status}
                                                </span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                    <div ref={sentinelRef} style={{ height: 10, opacity: 0 }} />
                </div>
            </div>

            {/* Task Detail Modal */}
            {selectedTask && isModalOpen && (
                <TaskDetailFullModal
                    isOpen={isModalOpen}
                    onClose={handleModalClose}
                    task={selectedTask}
                    listId={selectedTask.list_id}
                    statuses={DEFAULT_STATUSES}
                    members={members}
                    readOnly={isReadOnly}
                    onTaskUpdate={() => refreshTasks()}
                />
            )}
        </Layout>
    );
}
